import { LucideIcon, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { DashboardPanel } from "@/components/dashboard/DashboardPanel";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    direction: "up" | "down" | "neutral";
  };
  sparklineData?: number[];
  onClick?: () => void;
}

export function MetricCard({ title, value, icon: Icon, trend, sparklineData, onClick }: MetricCardProps) {
  const TrendIcon = trend?.direction === "up" ? TrendingUp : trend?.direction === "down" ? TrendingDown : Minus;
  const trendColor = trend?.direction === "up" ? "text-success" : trend?.direction === "down" ? "text-destructive" : "text-muted-foreground";

  return (
    <DashboardPanel
      className="group h-full cursor-pointer bg-gradient-to-br from-card via-card to-primary/5 transition-all duration-200 hover:border-primary/35 hover:shadow-[0_20px_45px_-28px_hsl(var(--shadow-color)/0.55)]"
      contentClassName="p-5"
    >
      <button onClick={onClick} className="block w-full text-left">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          {trend && (
            <div className={cn("flex items-center gap-1 rounded-full border border-border/60 bg-background/80 px-2.5 py-1 text-xs font-semibold", trendColor)}>
              <TrendIcon className="h-3.5 w-3.5" />
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>

        <div className="min-h-[4.5rem] space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{title}</p>
          <p className="truncate text-3xl font-semibold tracking-tight text-foreground">{value}</p>
        </div>

        {sparklineData && sparklineData.length > 0 && (
          <div className="mt-4 flex h-9 items-end gap-1">
            {sparklineData.map((val, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm bg-gradient-to-t from-primary/50 to-primary/15 transition-all duration-300 group-hover:from-primary/70"
                style={{ height: `${(val / Math.max(...sparklineData)) * 100}%`, minHeight: '4px' }}
              />
            ))}
          </div>
        )}
      </button>
    </DashboardPanel>
  );
}
